import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ColorTab } from "@/components/ui/color-tab";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FlaskConical, PackageCheck, Factory, ClipboardCheck, ShieldAlert, Boxes, GitBranch, RotateCw, Wrench, Beaker, MessageSquareWarning, FileWarning, ListChecks, Truck, Search, Sparkles, MessageCircle, TrendingUp, AlertTriangle, Clock, Send, BookOpen, Users } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { DeferredChart } from "../components/DeferredChart";
import { useAuth } from "@/lib/auth";

const cls = (s) => s || "";
const StatusBadge = ({ s }) => {
  const map = { pending: "bg-amber-100 text-amber-800", accepted: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-800", conditional: "bg-blue-100 text-blue-800", release: "bg-emerald-100 text-emerald-800", hold: "bg-amber-100 text-amber-800", reject: "bg-red-100 text-red-800", released: "bg-emerald-100 text-emerald-800", held: "bg-amber-100 text-amber-800", open: "bg-red-100 text-red-800", in_progress: "bg-blue-100 text-blue-800", verified: "bg-emerald-100 text-emerald-800", closed: "bg-gray-100 text-gray-800", investigating: "bg-blue-100 text-blue-800", resolved: "bg-emerald-100 text-emerald-800" };
  return <Badge className={`${map[s] || "bg-gray-100 text-gray-800"} border-0`}>{s || "—"}</Badge>;
};

export default function QCPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin" || user?.role_id === "super_admin";
  const [dash, setDash] = useState(null);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "rm";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { const t = searchParams.get("tab"); if (t) setTab(t); }, [searchParams]);
  useEffect(() => { api.get("/labqc/dashboard").then((r) => setDash(r.data)).catch(() => {}); }, []);
  return (
    <div className="p-6 space-y-6" data-testid="labqc-page">
      <div>
        <div className="uc-label">Quality Assurance</div>
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold text-[#2C1810] mt-1 flex items-center gap-2">
          <FlaskConical className="w-7 h-7 text-[#C05621]" /> Lab &amp; QC Management
        </h1>
        <p className="text-sm text-[#8B7355] mt-1">Every batch. Every sample. Every complaint. Traceable end-to-end.</p>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="qc-dashboard">
          <Stat label="Received today" value={dash.samples_received_today} />
          <Stat label="Pending" value={dash.samples_pending} tone="amber" />
          <Stat label="Approved" value={dash.batches_approved} tone="green" />
          <Stat label="Rejected" value={dash.batches_rejected} tone="red" />
          <Stat label="Hold stock" value={dash.hold_stock_count} tone="amber" />
          <Stat label="CAPA pending" value={dash.capa_pending} tone="amber" />
          <Stat label="Complaints" value={dash.complaints_open} tone="red" />
          <Stat label="Cal. due 14d" value={dash.equipment_calibration_due_14d} tone="amber" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto bg-transparent p-0 gap-0">
          <ColorTab value="rm" tone="blue" icon={PackageCheck} testid="tab-rm">Raw Material</ColorTab>
          <ColorTab value="pkg" tone="blue" testid="tab-pkg">Packaging</ColorTab>
          <ColorTab value="ipqc" tone="amber" icon={Factory} testid="tab-ipqc">In-Process</ColorTab>
          <ColorTab value="fpqc" tone="amber" testid="tab-fpqc">Finished</ColorTab>
          <ColorTab value="release" tone="green" icon={ClipboardCheck} testid="tab-release">Batch Release</ColorTab>
          <ColorTab value="holds" tone="red" icon={ShieldAlert} testid="tab-holds">Holds</ColorTab>
          <ColorTab value="shelf" tone="amber" testid="tab-shelf">Shelf-Life</ColorTab>
          <ColorTab value="samples" tone="purple" icon={Boxes} testid="tab-samples">Samples</ColorTab>
          <ColorTab value="ncr" tone="red" icon={FileWarning} testid="tab-ncr">NCR</ColorTab>
          <ColorTab value="capa" tone="green" icon={ListChecks} testid="tab-capa">CAPA</ColorTab>
          <ColorTab value="complaints" tone="red" icon={MessageSquareWarning} testid="tab-complaints">Complaints</ColorTab>
          <ColorTab value="trace" tone="blue" icon={GitBranch} testid="tab-trace">Traceability</ColorTab>
          <ColorTab value="recall" tone="red" icon={Truck} testid="tab-recall">Recall</ColorTab>
          <ColorTab value="suppliers" tone="purple" testid="tab-suppliers">Suppliers</ColorTab>
          <ColorTab value="equipment" tone="slate" icon={Wrench} testid="tab-equipment">Equipment</ColorTab>
          <ColorTab value="labinv" tone="purple" icon={Beaker} testid="tab-labinv">Lab Inventory</ColorTab>
          <ColorTab value="env" tone="blue" testid="tab-env">Env Monitor</ColorTab>
          <ColorTab value="hygiene" tone="green" testid="tab-hygiene">Hygiene Audit</ColorTab>
          <ColorTab value="ia" tone="slate" testid="tab-ia">Internal Audit</ColorTab>
          <ColorTab value="ccp" tone="orange" testid="tab-ccp">HACCP / CCP</ColorTab>
          <ColorTab value="recipe" tone="purple" testid="tab-recipe">Recipe Verify</ColorTab>
          <TabsTrigger value="aiai" data-testid="tab-aiai" className="bg-gradient-to-r from-purple-100 to-orange-100 data-[state=active]:from-purple-200 data-[state=active]:to-orange-200 border rounded-md px-3 py-1.5 mx-0.5 my-0.5 shadow-sm"><Sparkles className="w-3 h-3 mr-1 text-purple-700" /> AI Analytics</TabsTrigger>
          <TabsTrigger value="sopchat" data-testid="tab-sopchat" className="bg-gradient-to-r from-blue-100 to-emerald-100 data-[state=active]:from-blue-200 data-[state=active]:to-emerald-200 border rounded-md px-3 py-1.5 mx-0.5 my-0.5 shadow-sm"><MessageCircle className="w-3 h-3 mr-1 text-blue-700" /> SOP Chatbot</TabsTrigger>
          <TabsTrigger value="digest" data-testid="tab-digest" className="bg-gradient-to-r from-amber-100 to-red-100 data-[state=active]:from-amber-200 data-[state=active]:to-red-200 border rounded-md px-3 py-1.5 mx-0.5 my-0.5 shadow-sm"><Send className="w-3 h-3 mr-1 text-red-700" /> Weekly Digest</TabsTrigger>
          {isSuper && (
            <TabsTrigger value="team" data-testid="tab-team" className="bg-gradient-to-r from-slate-100 to-slate-200 data-[state=active]:from-slate-300 data-[state=active]:to-slate-400"><Users className="w-3 h-3 mr-1 text-slate-700" /> QC Team</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="rm"><CrudSection endpoint="/labqc/raw-material" title="Raw Material Inspections" fields={[
          { name: "material_name", label: "Material Name*", required: true },
          { name: "material_type", label: "Type", options: ["flour", "sugar", "butter", "chocolate", "dry_fruits", "other"] },
          { name: "batch_no", label: "Batch No*", required: true },
          { name: "supplier_name", label: "Supplier Name" },
          { name: "mfg_date", label: "Mfg Date", type: "date" },
          { name: "expiry_date", label: "Expiry", type: "date" },
          { name: "overall_status", label: "Status", options: ["pending", "accepted", "rejected", "conditional"] },
          { name: "remarks", label: "Remarks", multiline: true },
        ]} columns={["material_name", "batch_no", "supplier_name", "overall_status", "created_at"]} /></TabsContent>
        <TabsContent value="pkg"><CrudSection endpoint="/labqc/packaging" title="Packaging Inspections" fields={[
          { name: "packaging_type", label: "Type*", required: true, options: ["printed_pouch", "duplex_carton", "corrugated_carton", "label", "tin", "plastic_tray"] },
          { name: "supplier_name", label: "Supplier" },
          { name: "material_code", label: "Material Code" },
          { name: "overall_status", label: "Status", options: ["pending", "accepted", "rejected", "conditional"] },
          { name: "remarks", label: "Remarks", multiline: true },
        ]} columns={["packaging_type", "supplier_name", "overall_status", "created_at"]} /></TabsContent>
        <TabsContent value="ipqc"><CrudSection endpoint="/labqc/in-process" title="In-Process QC" fields={[
          { name: "production_batch_id", label: "Production Batch ID*", required: true },
          { name: "stage", label: "Stage", options: ["mixing", "baking", "cooling", "packing"] },
          { name: "overall_status", label: "Status", options: ["pending", "accepted", "rejected"] },
          { name: "remarks", label: "Remarks", multiline: true },
        ]} columns={["production_batch_id", "stage", "overall_status", "created_at"]} /></TabsContent>
        <TabsContent value="fpqc"><CrudSection endpoint="/labqc/finished" title="Finished Product QC" fields={[
          { name: "production_batch_id", label: "Production Batch ID*", required: true },
          { name: "overall_status", label: "Status", options: ["pending", "accepted", "rejected"] },
          { name: "remarks", label: "Remarks", multiline: true },
        ]} columns={["production_batch_id", "overall_status", "created_at"]} /></TabsContent>
        <TabsContent value="release"><BatchReleasePanel /></TabsContent>
        <TabsContent value="holds"><HoldsPanel /></TabsContent>
        <TabsContent value="shelf"><CrudSection endpoint="/labqc/shelf-life" title="Shelf-Life Studies" fields={[
          { name: "production_batch_id", label: "Production Batch ID*", required: true },
          { name: "product_id", label: "Product ID" },
          { name: "notes", label: "Notes", multiline: true },
        ]} columns={["production_batch_id", "started_at"]} /></TabsContent>
        <TabsContent value="samples"><CrudSection endpoint="/labqc/samples" title="Sample Retention" fields={[
          { name: "sample_type", label: "Type*", required: true, options: ["raw_material", "finished_goods", "complaint", "retention", "market", "export"] },
          { name: "reference_label", label: "Reference (batch/product)" },
          { name: "storage_location", label: "Storage Location" },
          { name: "expiry_date", label: "Sample Expiry", type: "date" },
          { name: "notes", label: "Notes", multiline: true },
        ]} columns={["sample_type", "barcode", "reference_label", "storage_location", "created_at"]} /></TabsContent>
        <TabsContent value="ncr"><CrudSection endpoint="/labqc/ncr" title="Non-Conformance Reports" fields={[
          { name: "title", label: "Title*", required: true },
          { name: "department", label: "Department*", required: true },
          { name: "target_date", label: "Target Close Date", type: "date" },
          { name: "root_cause", label: "Root Cause", multiline: true },
          { name: "notes", label: "Notes", multiline: true },
        ]} columns={["title", "department", "status", "target_date", "created_at"]} /></TabsContent>
        <TabsContent value="capa"><CrudSection endpoint="/labqc/capa" title="Corrective &amp; Preventive Actions" fields={[
          { name: "title", label: "Title*", required: true },
          { name: "corrective_action", label: "Corrective Action*", required: true, multiline: true },
          { name: "preventive_action", label: "Preventive Action", multiline: true },
          { name: "due_date", label: "Due Date*", type: "date", required: true },
        ]} columns={["title", "status", "due_date", "created_at"]} /></TabsContent>
        <TabsContent value="complaints"><CrudSection endpoint="/labqc/complaints" title="Customer Complaints" fields={[
          { name: "customer_type", label: "Customer Type", options: ["customer", "dealer", "retail", "export"] },
          { name: "customer_name", label: "Customer*", required: true },
          { name: "phone", label: "Phone" },
          { name: "invoice_number", label: "Invoice #" },
          { name: "complaint_type", label: "Complaint Type*", required: true, options: ["taste", "smell", "mold", "foreign_matter", "broken", "leakage", "wrong_label", "other"] },
          { name: "description", label: "Description*", required: true, multiline: true },
        ]} columns={["customer_name", "complaint_type", "batch_no", "status", "created_at"]} /></TabsContent>
        <TabsContent value="trace"><TraceabilityPanel /></TabsContent>
        <TabsContent value="recall"><RecallPanel /></TabsContent>
        <TabsContent value="suppliers"><SupplierScorecardPanel /></TabsContent>
        <TabsContent value="equipment"><EquipmentPanel /></TabsContent>
        <TabsContent value="labinv"><CrudSection endpoint="/labqc/inventory" title="Lab Inventory" fields={[
          { name: "name", label: "Item*", required: true },
          { name: "item_type", label: "Type", options: ["chemical", "media", "glassware", "pipette", "petri_plate", "standard", "other"] },
          { name: "unit", label: "Unit" },
          { name: "current_stock", label: "Current Stock", type: "number" },
          { name: "min_stock", label: "Min Stock", type: "number" },
          { name: "expiry_date", label: "Expiry", type: "date" },
        ]} columns={["name", "item_type", "current_stock", "min_stock", "expiry_date"]} /></TabsContent>
        <TabsContent value="env"><CrudSection endpoint="/labqc/env-readings" title="Environmental Monitoring" fields={[
          { name: "zone", label: "Zone*", required: true },
          { name: "reading_type", label: "Reading Type*", required: true, options: ["temperature", "humidity", "air_quality", "surface_swab", "water_quality", "hand_swab", "machine_swab", "air_plate_count"] },
          { name: "value", label: "Value*", type: "number", required: true },
          { name: "unit", label: "Unit" },
          { name: "spec_low", label: "Spec Low", type: "number" },
          { name: "spec_high", label: "Spec High", type: "number" },
          { name: "branch_id", label: "Branch (auto-hold scope)", helpText: "If set, only batches at this branch are auto-held on fail" },
          { name: "auto_hold", label: "Auto-hold active batches on fail", type: "boolean", defaultValue: true },
          { name: "taken_at", label: "Taken At", type: "datetime-local" },
          { name: "notes", label: "Notes", multiline: true },
        ]} columns={["zone", "reading_type", "value", "unit", "status", "auto_hold_applied", "taken_at"]} /></TabsContent>
        <TabsContent value="hygiene"><CrudSection endpoint="/labqc/hygiene-audits" title="Hygiene Audit" fields={[
          { name: "area", label: "Area*", required: true, options: ["factory", "staff", "machine", "production_area", "washroom", "pest_control"] },
          { name: "audit_date", label: "Audit Date", type: "date" },
          { name: "auditor", label: "Auditor" },
          { name: "score", label: "Score (0-100)", type: "number" },
          { name: "notes", label: "Notes", multiline: true },
        ]} columns={["area", "auditor", "score", "audit_date"]} /></TabsContent>
        <TabsContent value="ia"><CrudSection endpoint="/labqc/internal-audits" title="Internal Audit (ISO / FSSAI / HACCP / BRC)" fields={[
          { name: "audit_type", label: "Type*", required: true, options: ["iso", "fssai", "haccp", "brc", "customer"] },
          { name: "auditor_name", label: "Auditor" },
          { name: "audit_date", label: "Audit Date", type: "date" },
          { name: "scope", label: "Scope", multiline: true },
          { name: "overall_status", label: "Overall", options: ["pending", "accepted", "rejected", "conditional"] },
          { name: "notes", label: "Notes", multiline: true },
        ]} columns={["audit_type", "auditor_name", "overall_status", "status", "audit_date"]} /></TabsContent>
        <TabsContent value="ccp"><CrudSection endpoint="/labqc/ccp" title="HACCP · Critical Control Point Monitoring" fields={[
          { name: "ccp_name", label: "CCP Name*", required: true },
          { name: "stage", label: "Stage", options: ["receiving", "mixing", "baking", "cooling", "packing", "metal_detection", "storage"] },
          { name: "parameter", label: "Parameter*", required: true },
          { name: "critical_limit_low", label: "Limit Low", type: "number" },
          { name: "critical_limit_high", label: "Limit High", type: "number" },
          { name: "measured_value", label: "Measured Value*", type: "number", required: true },
          { name: "unit", label: "Unit" },
          { name: "corrective_action", label: "Corrective Action (if out of limit)", multiline: true },
          { name: "verified_by", label: "Verified By" },
        ]} columns={["ccp_name", "stage", "parameter", "measured_value", "status", "recorded_at"]} /></TabsContent>
        <TabsContent value="recipe"><CrudSection endpoint="/labqc/recipe-verifications" title="Recipe Verification (actual vs. standard)" fields={[
          { name: "production_batch_id", label: "Production Batch ID*", required: true },
          { name: "recipe_id", label: "Recipe ID" },
          { name: "notes", label: "Notes", multiline: true },
        ]} columns={["production_batch_id", "recipe_id", "max_deviation_pct", "overall_status", "verified_at"]} /></TabsContent>
        <TabsContent value="aiai"><AIAnalyticsPanel /></TabsContent>
        <TabsContent value="sopchat"><SOPChatPanel /></TabsContent>
        <TabsContent value="digest"><WeeklyDigestPanel /></TabsContent>
        {isSuper && <TabsContent value="team"><QCTeamPanel /></TabsContent>}
      </Tabs>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const c = tone === "green" ? "text-emerald-700" : tone === "red" ? "text-red-700" : tone === "amber" ? "text-amber-700" : "text-[#2C1810]";
  return (
    <div className="border border-[#EAE5D9] rounded-md p-3 bg-white">
      <div className="text-[10px] uppercase tracking-wider text-[#8B7355]">{label}</div>
      <div className={`text-2xl font-[Outfit] font-semibold ${c}`}>{value ?? 0}</div>
    </div>
  );
}

function CrudSection({ endpoint, title, fields, columns }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = () => api.get(endpoint).then((r) => setRows(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, [endpoint]);
  const submit = async () => {
    const missing = fields.filter((f) => f.required && !form[f.name]);
    if (missing.length) { toast.error("Fill: " + missing.map((f) => f.label).join(", ")); return; }
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "number" && payload[f.name] != null && payload[f.name] !== "") payload[f.name] = Number(payload[f.name]);
        if (f.type === "boolean") payload[f.name] = payload[f.name] === undefined ? (f.defaultValue ?? false) : !!payload[f.name];
      });
      await api.post(endpoint, payload);
      toast.success("Saved");
      setOpen(false); setForm({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="pt-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-[Outfit] text-lg font-semibold">{title}</h3>
        <Button onClick={() => setOpen(true)} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid={`add-${endpoint.split("/").pop()}`}>+ New</Button>
      </div>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase">
            <tr>{columns.map((c) => <th key={c} className="text-left px-3 py-2">{c.replace(/_/g, " ")}</th>)}</tr>
          </thead>
          <tbody data-testid={`rows-${endpoint.split("/").pop()}`}>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9]">
                {columns.map((c) => {
                  let v = r[c];
                  if (c === "created_at" || c === "started_at" || c === "target_date" || c === "due_date" || c === "expiry_date") v = v ? new Date(v).toLocaleDateString("en-IN") : "—";
                  if (c === "overall_status" || c === "status") return <td key={c} className="px-3 py-2"><StatusBadge s={v} /></td>;
                  if (typeof v === "boolean") return <td key={c} className="px-3 py-2">{v ? <Badge className="bg-red-100 text-red-800 border-0">yes</Badge> : <span className="text-[#8B7355]">—</span>}</td>;
                  return <td key={c} className="px-3 py-2">{v ?? "—"}</td>;
                })}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-[#8B7355]">No records</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle className="font-[Outfit]">New · {title}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            {fields.map((f) => (
              <div key={f.name} className={f.multiline || f.type === "boolean" ? "col-span-2" : ""}>
                <Label>{f.label}</Label>
                {f.options ? (
                  <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-white">{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.multiline ? (
                  <Textarea rows={2} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`field-${f.name}`} />
                ) : f.type === "boolean" ? (
                  <label className="flex items-center gap-2 mt-1 text-sm">
                    <input type="checkbox" checked={form[f.name] === undefined ? !!f.defaultValue : !!form[f.name]}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })}
                      data-testid={`field-${f.name}`} className="w-4 h-4" />
                    <span className="text-[#8B7355]">enable</span>
                  </label>
                ) : (
                  <>
                    <Input type={f.type || "text"} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`field-${f.name}`} />
                    {f.helpText && <p className="text-[10px] text-[#8B7355] mt-1">{f.helpText}</p>}
                  </>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-[#2C1810] text-white" data-testid="submit-crud">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BatchReleasePanel() {
  const [rows, setRows] = useState([]);
  const [batchId, setBatchId] = useState(""); const [decision, setDecision] = useState("release"); const [reason, setReason] = useState("");
  const load = () => api.get("/labqc/batch-release").then((r) => setRows(r.data)).catch(() => {});
  useEffect(load, []);
  const submit = async () => {
    if (!batchId) { toast.error("Enter batch id"); return; }
    try { await api.post("/labqc/batch-release", { production_batch_id: batchId, decision, reason }); toast.success(`Decision ${decision}`); setBatchId(""); setReason(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="pt-4 space-y-3">
      <h3 className="font-[Outfit] text-lg font-semibold">Batch Release Gate</h3>
      <p className="text-sm text-[#8B7355]">Production batches require QC decision before they can dispatch. On <b>reject</b> we auto-create a hold and a CAPA.</p>
      <div className="grid grid-cols-4 gap-2 items-end p-3 rounded-md bg-[#FAF6EE] border border-[#EAE5D9]">
        <div className="col-span-2"><Label>Production Batch ID</Label><Input value={batchId} onChange={(e) => setBatchId(e.target.value)} data-testid="release-batch-id" /></div>
        <div><Label>Decision</Label>
          <Select value={decision} onValueChange={setDecision}>
            <SelectTrigger data-testid="release-decision"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="release">release</SelectItem><SelectItem value="hold">hold</SelectItem><SelectItem value="reject">reject</SelectItem></SelectContent>
          </Select>
        </div>
        <Button onClick={submit} className="bg-[#C05621] text-white" data-testid="release-submit">Post decision</Button>
        <div className="col-span-4"><Label>Reason (required for hold/reject)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="release-reason" /></div>
      </div>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr><th className="text-left px-3 py-2">Batch</th><th className="text-left px-3 py-2">Product</th><th className="text-left px-3 py-2">Decision</th><th className="text-left px-3 py-2">Reason</th><th className="text-left px-3 py-2">By</th><th className="text-left px-3 py-2">When</th></tr></thead>
          <tbody data-testid="release-rows">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9]"><td className="px-3 py-2 font-mono text-xs">{r.batch_no || r.production_batch_id}</td><td className="px-3 py-2">{r.product_name || "—"}</td><td className="px-3 py-2"><StatusBadge s={r.decision} /></td><td className="px-3 py-2 text-xs">{r.reason || "—"}</td><td className="px-3 py-2 text-xs">{r.released_by}</td><td className="px-3 py-2 text-xs text-[#8B7355]">{new Date(r.released_at).toLocaleString("en-IN")}</td></tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#8B7355]">No decisions yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HoldsPanel() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/labqc/holds").then((r) => setRows(r.data)).catch(() => {});
  useEffect(load, []);
  const release = async (hid) => { try { await api.post(`/labqc/holds/${hid}/release`); toast.success("Hold released"); load(); } catch (e) { toast.error("Failed"); } };
  return (
    <div className="pt-4 space-y-3">
      <h3 className="font-[Outfit] text-lg font-semibold">Held / Rejected Stock</h3>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr><th className="text-left px-3 py-2">Batch</th><th className="text-left px-3 py-2">Reason</th><th className="text-left px-3 py-2">Category</th><th className="text-left px-3 py-2">Status</th><th className="px-3 py-2"></th></tr></thead>
          <tbody data-testid="holds-rows">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9]">
                <td className="px-3 py-2 font-mono text-xs">{r.batch_no || r.production_batch_id}</td>
                <td className="px-3 py-2">{r.reason}</td>
                <td className="px-3 py-2 text-xs">{r.reason_category}</td>
                <td className="px-3 py-2"><StatusBadge s={r.status} /></td>
                <td className="px-3 py-2 text-right">{r.status !== "released" && <Button size="sm" variant="outline" onClick={() => release(r.id)} data-testid={`release-hold-${r.id}`}>Release</Button>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-[#8B7355]">Nothing on hold — 🎉</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TraceabilityPanel() {
  const [q, setQ] = useState(""); const [trace, setTrace] = useState(null);
  const run = async () => {
    if (!q.trim()) return;
    try { const r = await api.get(`/labqc/traceability`, { params: { batch: q.trim() } }); setTrace(r.data); toast.success("Trace loaded"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Not found"); setTrace(null); }
  };
  return (
    <div className="pt-4 space-y-3">
      <h3 className="font-[Outfit] text-lg font-semibold">One-Click Traceability</h3>
      <div className="flex gap-2 items-end p-3 rounded-md bg-[#FAF6EE] border border-[#EAE5D9]">
        <div className="flex-1"><Label>Batch ID or Batch No</Label><Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} data-testid="trace-input" placeholder="e.g. BMF/PB/26/0001" /></div>
        <Button onClick={run} className="bg-[#C05621] text-white" data-testid="trace-run"><Search className="w-3 h-3 mr-1" /> Trace</Button>
      </div>
      {trace && (
        <div className="space-y-3" data-testid="trace-result">
          <Card className="border-[#EAE5D9]"><CardHeader><CardTitle className="font-[Outfit] text-lg">Batch {trace.batch.batch_no}</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1">
              <div><b>Product:</b> {trace.batch.product_name}</div>
              <div><b>Qty:</b> {trace.batch.quantity} · <b>Mfg:</b> {trace.batch.mfg_date} · <b>Exp:</b> {trace.batch.expiry_date}</div>
              <div><b>Status:</b> <StatusBadge s={trace.batch.status} /> · <b>QC:</b> <StatusBadge s={trace.batch.qc_status} /></div>
            </CardContent>
          </Card>
          <Card className="border-[#EAE5D9]"><CardHeader><CardTitle className="text-base">Ingredients used ({trace.ingredients_used.length})</CardTitle></CardHeader>
            <CardContent className="text-xs">{trace.ingredients_used.map((i, k) => <div key={k}>· {i.ingredient_name || i.name} × {i.quantity} {i.unit}</div>)}</CardContent>
          </Card>
          <Card className="border-[#EAE5D9]"><CardHeader><CardTitle className="text-base">QC records</CardTitle></CardHeader>
            <CardContent className="text-xs space-y-1">
              <div>In-process: {trace.in_process_qc.length} · Finished: {trace.finished_product_qc.length} · Holds: {trace.holds.length}</div>
              {trace.release_decision && <div>Release: <StatusBadge s={trace.release_decision.decision} /> — {trace.release_decision.reason}</div>}
            </CardContent>
          </Card>
          <Card className="border-[#EAE5D9]"><CardHeader><CardTitle className="text-base">Customer reach ({trace.customer_sales.length})</CardTitle></CardHeader>
            <CardContent className="text-xs max-h-56 overflow-y-auto">{trace.customer_sales.map((s) => <div key={s.id}>{s.number} · {s.customer_snapshot?.name || "Walk-in"} · {s.channel} · {new Date(s.created_at).toLocaleDateString("en-IN")}</div>)}</CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function RecallPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/labqc/recalls").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="pt-4 space-y-3">
      <h3 className="font-[Outfit] text-lg font-semibold">Batch Recalls</h3>
      <p className="text-xs text-[#8B7355]">Recalls flow from Manufacturing → Batches. Once issued, POS blocks the batch, warehouses mark stock recalled, and affected customers appear here for outreach.</p>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden">
        <table className="w-full text-sm"><thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr><th className="text-left px-3 py-2">Batch</th><th className="text-left px-3 py-2">Reason</th><th className="text-left px-3 py-2">Affected</th><th className="text-left px-3 py-2">When</th></tr></thead>
          <tbody data-testid="recall-rows">
            {rows.map((r) => (<tr key={r.id} className="border-t border-[#EAE5D9]"><td className="px-3 py-2 font-mono text-xs">{r.batch_no}</td><td className="px-3 py-2">{r.reason}</td><td className="px-3 py-2">{r.affected_customers?.length || 0}</td><td className="px-3 py-2 text-xs">{new Date(r.issued_at).toLocaleString("en-IN")}</td></tr>))}
            {rows.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-[#8B7355]">No recalls</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SupplierScorecardPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/labqc/suppliers/scorecard").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="pt-4 space-y-3">
      <h3 className="font-[Outfit] text-lg font-semibold">Supplier Quality Scorecard</h3>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden">
        <table className="w-full text-sm"><thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr><th className="text-left px-3 py-2">Supplier</th><th className="text-right px-3 py-2">Inspections</th><th className="text-right px-3 py-2">Accepted</th><th className="text-right px-3 py-2">Rejected</th><th className="text-right px-3 py-2">Score</th></tr></thead>
          <tbody data-testid="supp-rows">
            {rows.map((r) => (<tr key={r.supplier_id} className="border-t border-[#EAE5D9]"><td className="px-3 py-2">{r.supplier_name || r.supplier_id}</td><td className="px-3 py-2 text-right">{r.total_inspections}</td><td className="px-3 py-2 text-right text-emerald-700">{r.acceptances}</td><td className="px-3 py-2 text-right text-red-700">{r.rejections}</td><td className="px-3 py-2 text-right font-semibold">{r.quality_score}%</td></tr>))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-[#8B7355]">No supplier data yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EquipmentPanel() {
  const [rows, setRows] = useState([]); const [open, setOpen] = useState(false); const [form, setForm] = useState({});
  const load = () => api.get("/labqc/equipment").then((r) => setRows(r.data)).catch(() => {}); useEffect(load, []);
  const submit = async () => {
    if (!form.name || !form.equipment_type) { toast.error("Name + type required"); return; }
    try { await api.post("/labqc/equipment", { ...form, calibration_frequency_days: Number(form.calibration_frequency_days) || 365 }); toast.success("Saved"); setOpen(false); setForm({}); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="pt-4 space-y-3">
      <div className="flex items-center justify-between"><h3 className="font-[Outfit] text-lg font-semibold">Lab Equipment &amp; Calibration</h3><Button onClick={() => setOpen(true)} className="bg-[#C05621] text-white" data-testid="add-equipment">+ Add</Button></div>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden">
        <table className="w-full text-sm"><thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">S/N</th><th className="text-left px-3 py-2">Next Cal.</th></tr></thead>
          <tbody data-testid="equip-rows">
            {rows.map((r) => (<tr key={r.id} className="border-t border-[#EAE5D9]"><td className="px-3 py-2">{r.name}</td><td className="px-3 py-2 text-xs">{r.equipment_type}</td><td className="px-3 py-2 font-mono text-xs">{r.serial_number || "—"}</td><td className="px-3 py-2 text-xs">{r.next_calibration_due || "—"}</td></tr>))}
            {rows.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-[#8B7355]">No equipment yet</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Add Equipment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><Label>Name*</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="equip-name" /></div>
            <div><Label>Type*</Label><Input value={form.equipment_type || ""} onChange={(e) => setForm({ ...form, equipment_type: e.target.value })} placeholder="Moisture Analyzer / pH Meter…" data-testid="equip-type" /></div>
            <div><Label>Serial Number</Label><Input value={form.serial_number || ""} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></div>
            <div><Label>Location</Label><Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            <div><Label>Cal. freq (days)</Label><Input type="number" value={form.calibration_frequency_days || 365} onChange={(e) => setForm({ ...form, calibration_frequency_days: e.target.value })} /></div>
            <div><Label>Next due</Label><Input type="date" value={form.next_calibration_due || ""} onChange={(e) => setForm({ ...form, next_calibration_due: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#2C1810] text-white" data-testid="equip-submit">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// =====================================================================
// Phase 19-E · AI Quality Analytics
// =====================================================================
function AIAnalyticsPanel() {
  const [days, setDays] = useState(90);
  const [trends, setTrends] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loadingTrends, setLoadingTrends] = useState(false);

  const loadAll = async () => {
    setLoadingTrends(true);
    try {
      const [t, s, p] = await Promise.all([
        api.get(`/qcai/trends?days=${days}`),
        api.get(`/qcai/supplier-quality?days=${days}`),
        api.get(`/qcai/predictions?limit=20`),
      ]);
      setTrends(t.data); setSuppliers(s.data || []); setPredictions(p.data || []);
    } catch (e) { toast.error("Failed to load AI analytics"); }
    finally { setLoadingTrends(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [days]);

  // Merge daily series into one array for line chart
  const mergedDaily = useMemo(() => {
    if (!trends) return [];
    const all = new Set();
    Object.values(trends.daily || {}).forEach((series) => (series || []).forEach((row) => all.add(row.date)));
    const dates = Array.from(all).sort();
    const idx = {};
    Object.entries(trends.daily || {}).forEach(([k, series]) => {
      idx[k] = {}; (series || []).forEach((row) => { idx[k][row.date] = row.count; });
    });
    return dates.map((d) => ({
      date: d.slice(5),
      NCR: idx.ncr?.[d] || 0,
      Holds: idx.holds?.[d] || 0,
      Complaints: idx.complaints?.[d] || 0,
      CCPfails: idx.ccp_fails?.[d] || 0,
      EnvFails: idx.env_fails?.[d] || 0,
    }));
  }, [trends]);

  return (
    <div className="pt-4 space-y-6" data-testid="ai-analytics-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-[Outfit] text-xl font-semibold text-[#2C1810] flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-700" /> AI Quality Analytics</h3>
          <p className="text-xs text-[#8B7355] mt-1">Claude Sonnet 4.5 — batch-failure prediction, shelf-life predictor, quality trends &amp; supplier scorecard.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Window</Label>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-28 h-8" data-testid="aiai-window"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="60">60 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
              <SelectItem value="180">180 days</SelectItem>
              <SelectItem value="365">365 days</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={loadAll} data-testid="aiai-refresh"><RotateCw className="w-3 h-3 mr-1" /> Refresh</Button>
        </div>
      </div>

      {/* Totals strip */}
      {trends && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="aiai-totals">
          <MiniStat label="NCR" value={trends.totals.ncr} tone="red" />
          <MiniStat label="Holds" value={trends.totals.holds} tone="amber" />
          <MiniStat label="Complaints" value={trends.totals.complaints} tone="red" />
          <MiniStat label="CCP fails" value={trends.totals.ccp_fails} tone="red" />
          <MiniStat label="Env fails" value={trends.totals.env_fails} tone="amber" />
          <MiniStat label="Released" value={trends.totals.batches_released} tone="green" />
          <MiniStat label="Held" value={trends.totals.batches_held} tone="amber" />
          <MiniStat label="Rejected" value={trends.totals.batches_rejected} tone="red" />
        </div>
      )}

      {/* Trend chart */}
      <Card className="border-[#EAE5D9]">
        <CardHeader className="pb-2"><CardTitle className="text-base font-[Outfit] flex items-center gap-2"><TrendingUp className="w-4 h-4 text-purple-700" /> Daily Quality Signals</CardTitle></CardHeader>
        <CardContent>
          {loadingTrends ? <div className="text-xs text-[#8B7355] py-8 text-center">Loading…</div> :
            mergedDaily.length === 0 ? <div className="text-xs text-[#8B7355] py-8 text-center">No events in window</div> :
            <DeferredChart height={260} className="mt-1" data-testid="aiai-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={mergedDaily} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE5D9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="NCR" stroke="#DC2626" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Holds" stroke="#D97706" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Complaints" stroke="#7C2D12" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="CCPfails" stroke="#7C3AED" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="EnvFails" stroke="#0891B2" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </DeferredChart>
          }
        </CardContent>
      </Card>

      {/* Two-column: complaints by type & rejected suppliers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-[#EAE5D9]">
          <CardHeader className="pb-2"><CardTitle className="text-base font-[Outfit]">Top Complaint Types</CardTitle></CardHeader>
          <CardContent>
            {trends?.top_complaint_types?.length ? (
              <DeferredChart height={220}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trends.top_complaint_types} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE5D9" />
                    <XAxis dataKey="type" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="count" fill="#C05621" />
                  </BarChart>
                </ResponsiveContainer>
              </DeferredChart>
            ) : <div className="text-xs text-[#8B7355] py-8 text-center">No complaints in window</div>}
          </CardContent>
        </Card>
        <Card className="border-[#EAE5D9]">
          <CardHeader className="pb-2"><CardTitle className="text-base font-[Outfit]">Top Complaint SKUs</CardTitle></CardHeader>
          <CardContent>
            {trends?.top_complaint_skus?.length ? (
              <table className="w-full text-sm">
                <tbody>
                  {trends.top_complaint_skus.map((r, k) => (
                    <tr key={k} className="border-b border-[#EAE5D9] last:border-0">
                      <td className="py-1.5">{r.product}</td>
                      <td className="py-1.5 text-right font-semibold text-red-700">{r.complaints}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="text-xs text-[#8B7355] py-8 text-center">—</div>}
          </CardContent>
        </Card>
      </div>

      {/* Supplier AI Scorecard */}
      <Card className="border-[#EAE5D9]">
        <CardHeader className="pb-2"><CardTitle className="text-base font-[Outfit]">Supplier Quality Scorecard (AI-graded)</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="aiai-supplier-rows">
            <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase">
              <tr><th className="text-left px-3 py-2">Supplier</th><th className="text-right px-3 py-2">Inspections</th><th className="text-right px-3 py-2">Accepted</th><th className="text-right px-3 py-2">Rejected</th><th className="text-right px-3 py-2">Rejection %</th><th className="text-right px-3 py-2">Score</th><th className="text-center px-3 py-2">Grade</th></tr>
            </thead>
            <tbody>
              {suppliers.map((r) => (
                <tr key={r.supplier_id} className="border-t border-[#EAE5D9]">
                  <td className="px-3 py-2">{r.supplier_name}</td>
                  <td className="px-3 py-2 text-right">{r.total_inspections}</td>
                  <td className="px-3 py-2 text-right text-emerald-700">{r.accepted}</td>
                  <td className="px-3 py-2 text-right text-red-700">{r.rejected}</td>
                  <td className="px-3 py-2 text-right">{r.rejection_rate_pct}%</td>
                  <td className="px-3 py-2 text-right font-semibold">{r.quality_score}</td>
                  <td className="px-3 py-2 text-center"><Badge className={`border-0 ${r.grade.startsWith("A") ? "bg-emerald-100 text-emerald-800" : r.grade === "B" ? "bg-blue-100 text-blue-800" : r.grade === "C" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{r.grade}</Badge></td>
                </tr>
              ))}
              {suppliers.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#8B7355]">No supplier RM data in window</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Batch failure prediction */}
      <BatchFailurePredictor onSaved={loadAll} />

      {/* Shelf-life predictor */}
      <ShelfLifePredictor />

      {/* Recent predictions */}
      <Card className="border-[#EAE5D9]">
        <CardHeader className="pb-2"><CardTitle className="text-base font-[Outfit] flex items-center gap-2"><Clock className="w-4 h-4" /> Recent AI Predictions</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="aiai-predictions-rows">
            <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Kind</th><th className="text-left px-3 py-2">Batch / Product</th><th className="text-left px-3 py-2">Summary</th></tr></thead>
            <tbody>
              {predictions.map((p) => (
                <tr key={p.id} className="border-t border-[#EAE5D9]">
                  <td className="px-3 py-2 text-xs">{new Date(p.generated_at).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 text-xs"><Badge className={`border-0 ${p.kind === "batch_failure" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"}`}>{p.kind}</Badge></td>
                  <td className="px-3 py-2 font-mono text-xs">{p.batch_no || p.product_name || "—"}</td>
                  <td className="px-3 py-2 text-xs">{p.prediction?.risk_level ? <>Risk <b>{p.prediction.risk_score}</b> · {p.prediction.risk_level}</> : p.prediction?.predicted_shelf_life_days != null ? <>Shelf-life <b>{p.prediction.predicted_shelf_life_days}d</b> (adj {p.prediction.adjustment_days}d)</> : "—"}</td>
                </tr>
              ))}
              {predictions.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-[#8B7355]">No predictions yet — run one above</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const c = tone === "green" ? "text-emerald-700" : tone === "red" ? "text-red-700" : tone === "amber" ? "text-amber-700" : "text-[#2C1810]";
  return (
    <div className="border border-[#EAE5D9] rounded-md p-3 bg-white">
      <div className="text-[10px] uppercase tracking-wider text-[#8B7355]">{label}</div>
      <div className={`text-2xl font-[Outfit] font-semibold ${c}`}>{value ?? 0}</div>
    </div>
  );
}

function BatchFailurePredictor({ onSaved }) {
  const [batchId, setBatchId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!batchId.trim()) { toast.error("Enter a production batch ID"); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.post(`/qcai/predict-failure/${batchId.trim()}`);
      setResult(r.data);
      toast.success("AI risk analysis complete");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Prediction failed"); }
    finally { setLoading(false); }
  };

  const p = result?.prediction;
  const levelClr = p?.risk_level === "critical" ? "bg-red-600 text-white" : p?.risk_level === "high" ? "bg-red-100 text-red-800" : p?.risk_level === "medium" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800";

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50/40 to-white">
      <CardHeader className="pb-2"><CardTitle className="text-base font-[Outfit] flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-purple-700" /> Batch Failure Risk Predictor</CardTitle></CardHeader>
      <CardContent>
        <div className="flex gap-2 items-end mb-3">
          <div className="flex-1"><Label>Production Batch ID</Label><Input value={batchId} onChange={(e) => setBatchId(e.target.value)} placeholder="paste UUID from Production module" data-testid="failure-batch-id" /></div>
          <Button onClick={run} disabled={loading} className="bg-purple-700 hover:bg-purple-800 text-white" data-testid="failure-run">{loading ? "Analysing…" : <><Sparkles className="w-3 h-3 mr-1" /> Analyse</>}</Button>
        </div>
        {p && (
          <div className="space-y-3 pt-2" data-testid="failure-result">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge className={`${levelClr} border-0 px-3 py-1 text-sm`}>Risk {p.risk_score} / 100 — {p.risk_level}</Badge>
              <Badge className="bg-blue-100 text-blue-800 border-0">Confidence: {p.confidence}</Badge>
              <span className="text-xs text-[#8B7355]">Batch <b className="font-mono">{result.batch_no}</b> · {result.product_name}</span>
            </div>
            <p className="text-sm text-[#2C1810] italic border-l-2 border-purple-300 pl-3">"{p.reasoning}"</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8B7355] mb-1">Top Risk Factors</div>
                <ul className="space-y-1">
                  {(p.top_risk_factors || []).map((f, k) => (
                    <li key={k} className="text-sm flex gap-2 items-start">
                      <Badge className={`border-0 text-[10px] ${f.impact === "high" ? "bg-red-100 text-red-800" : f.impact === "medium" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>{f.impact}</Badge>
                      <span><b>{f.factor}</b> — <span className="text-[#8B7355]">{f.detail}</span></span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8B7355] mb-1">Recommended Actions</div>
                <ul className="space-y-1">
                  {(p.recommended_actions || []).map((a, k) => (
                    <li key={k} className="text-sm flex gap-2 items-start">
                      <Badge className={`border-0 text-[10px] ${a.priority === "urgent" ? "bg-red-600 text-white" : a.priority === "high" ? "bg-red-100 text-red-800" : a.priority === "medium" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>{a.priority}</Badge>
                      <span>{a.action} <span className="text-[#8B7355] text-xs">({a.owner})</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            {p.likely_failure_modes?.length ? (
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8B7355] mb-1">Likely Failure Modes</div>
                <div className="flex gap-2 flex-wrap">{p.likely_failure_modes.map((m, k) => <Badge key={k} className="bg-orange-100 text-orange-800 border-0">{m}</Badge>)}</div>
              </div>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ShelfLifePredictor() {
  const [batchId, setBatchId] = useState("");
  const [productId, setProductId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!batchId.trim() && !productId.trim()) { toast.error("Enter a batch id or product id"); return; }
    setLoading(true); setResult(null);
    try {
      const body = {}; if (batchId.trim()) body.production_batch_id = batchId.trim(); if (productId.trim()) body.product_id = productId.trim();
      const r = await api.post("/qcai/predict-shelf-life", body);
      setResult(r.data);
      toast.success("Shelf-life predicted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Prediction failed"); }
    finally { setLoading(false); }
  };
  const p = result?.prediction;

  return (
    <Card className="border-orange-200 bg-gradient-to-br from-orange-50/40 to-white">
      <CardHeader className="pb-2"><CardTitle className="text-base font-[Outfit] flex items-center gap-2"><Clock className="w-4 h-4 text-orange-700" /> Shelf-Life Predictor</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end mb-3">
          <div><Label>Production Batch ID</Label><Input value={batchId} onChange={(e) => setBatchId(e.target.value)} placeholder="(optional)" data-testid="shelf-batch-id" /></div>
          <div><Label>Product ID</Label><Input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="(optional)" data-testid="shelf-product-id" /></div>
          <Button onClick={run} disabled={loading} className="bg-orange-700 hover:bg-orange-800 text-white" data-testid="shelf-run">{loading ? "Predicting…" : <><Sparkles className="w-3 h-3 mr-1" /> Predict</>}</Button>
        </div>
        {p && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="shelf-result">
            <div className="border border-orange-200 rounded-md p-3 bg-white">
              <div className="text-[10px] uppercase text-[#8B7355]">Declared</div>
              <div className="text-2xl font-[Outfit] font-semibold text-[#2C1810]">{p.declared_shelf_life_days ?? "—"}d</div>
            </div>
            <div className="border border-orange-200 rounded-md p-3 bg-white">
              <div className="text-[10px] uppercase text-[#8B7355]">AI Predicted</div>
              <div className="text-2xl font-[Outfit] font-semibold text-orange-700">{p.predicted_shelf_life_days ?? "—"}d</div>
            </div>
            <div className="border border-orange-200 rounded-md p-3 bg-white">
              <div className="text-[10px] uppercase text-[#8B7355]">Adjustment</div>
              <div className={`text-2xl font-[Outfit] font-semibold ${(p.adjustment_days || 0) < 0 ? "text-red-700" : "text-emerald-700"}`}>{p.adjustment_days > 0 ? "+" : ""}{p.adjustment_days ?? 0}d</div>
            </div>
            <div className="border border-orange-200 rounded-md p-3 bg-white">
              <div className="text-[10px] uppercase text-[#8B7355]">Spoilage Risk</div>
              <div className="text-2xl font-[Outfit] font-semibold text-red-700">{p.risk_of_early_spoilage_pct ?? 0}%</div>
            </div>
            <div className="col-span-full">
              <div className="text-xs uppercase text-[#8B7355] mb-1">Primary Drivers</div>
              <div className="flex gap-2 flex-wrap">
                {(p.primary_drivers || []).map((d, k) => (
                  <Badge key={k} className={`border-0 ${d.direction === "reduces" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>{d.direction === "reduces" ? "▼" : "▲"} {d.driver} ({d.magnitude})</Badge>
                ))}
              </div>
              <p className="text-sm mt-2 italic border-l-2 border-orange-300 pl-3">"{p.reasoning}"</p>
              {p.recommended_storage_notes && <p className="text-xs mt-2 text-[#8B7355]"><b>Storage:</b> {p.recommended_storage_notes}</p>}
              {p.recommended_reinspection_day != null && <p className="text-xs text-[#8B7355]"><b>Recommended reinspection at day:</b> {p.recommended_reinspection_day}</p>}
              <Badge className="mt-2 bg-blue-100 text-blue-800 border-0">Confidence: {p.confidence}</Badge>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Phase 19-E · SOP Chatbot
// =====================================================================
function SOPChatPanel() {
  const [sops, setSops] = useState([]);
  const [sopOpen, setSopOpen] = useState(false);
  const [sopForm, setSopForm] = useState({ category: "raw_material", tags: [] });
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessions, setSessions] = useState([]);

  const loadSops = () => api.get("/qcai/sops").then((r) => setSops(r.data)).catch(() => {});
  const loadSessions = () => api.get("/qcai/sop-chat-sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { loadSops(); loadSessions(); }, []);

  const seedDefaults = async () => {
    try { const r = await api.post("/qcai/sops/seed"); toast.success(`Seeded ${r.data.inserted} SOPs`); loadSops(); }
    catch (e) { toast.error("Seed failed"); }
  };
  const saveSop = async () => {
    if (!sopForm.code || !sopForm.title || !sopForm.body) { toast.error("code / title / body required"); return; }
    try {
      const payload = { ...sopForm, tags: (sopForm.tags || []) };
      await api.post("/qcai/sops", payload);
      toast.success("SOP saved");
      setSopOpen(false); setSopForm({ category: "raw_material", tags: [] }); loadSops();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deleteSop = async (id) => {
    if (!window.confirm("Delete this SOP?")) return;
    try { await api.delete(`/qcai/sops/${id}`); toast.success("Deleted"); loadSops(); } catch { toast.error("Delete failed"); }
  };

  const send = async () => {
    const text = input.trim(); if (!text) return;
    setSending(true);
    const userMsg = { role: "user", text, at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    try {
      const body = { message: text }; if (sessionId) body.session_id = sessionId;
      const r = await api.post("/qcai/sop-chat", body);
      if (!sessionId) setSessionId(r.data.session_id);
      setMessages((m) => [...m, r.data.assistant_message]);
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chat failed");
      setMessages((m) => [...m, { role: "assistant", text: "(error — try again)", at: new Date().toISOString() }]);
    } finally { setSending(false); }
  };

  const openSession = async (sid) => {
    try {
      const r = await api.get(`/qcai/sop-chat/${sid}`);
      setSessionId(sid);
      setMessages(r.data.messages || []);
    } catch { toast.error("Session load failed"); }
  };

  const newChat = () => { setSessionId(null); setMessages([]); };

  return (
    <div className="pt-4 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="sop-chat-panel">
      {/* SOP Knowledge Base */}
      <Card className="border-[#EAE5D9] lg:col-span-1">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-[Outfit] flex items-center gap-2"><BookOpen className="w-4 h-4 text-blue-700" /> SOP Library</CardTitle>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={seedDefaults} data-testid="sop-seed" title="Seed default SOPs">Seed</Button>
              <Button size="sm" className="bg-[#C05621] text-white" onClick={() => setSopOpen(true)} data-testid="sop-new">+ New</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[420px] overflow-y-auto divide-y divide-[#EAE5D9]" data-testid="sop-list">
            {sops.map((s) => (
              <div key={s.id} className="p-3 hover:bg-[#FAF6EE]">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono text-purple-700">{s.code}</div>
                    <div className="text-sm font-semibold text-[#2C1810]">{s.title}</div>
                    <Badge className="bg-blue-100 text-blue-800 border-0 mt-1 text-[10px]">{s.category}</Badge>
                    <p className="text-xs text-[#8B7355] mt-1 line-clamp-3 whitespace-pre-wrap">{s.body}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => deleteSop(s.id)} className="text-red-600 h-6 px-2" data-testid={`sop-del-${s.code}`}>×</Button>
                </div>
              </div>
            ))}
            {sops.length === 0 && <div className="p-8 text-center text-xs text-[#8B7355]">No SOPs yet — click <b>Seed</b> to load defaults, or <b>+ New</b> to upload your own.</div>}
          </div>
        </CardContent>
      </Card>

      {/* Chat Panel */}
      <Card className="border-[#EAE5D9] lg:col-span-2">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-[Outfit] flex items-center gap-2"><MessageCircle className="w-4 h-4 text-emerald-700" /> Ask your QA Assistant</CardTitle>
            <div className="flex gap-1 items-center">
              <Select value={sessionId || ""} onValueChange={(v) => v ? openSession(v) : null}>
                <SelectTrigger className="w-56 h-8 text-xs" data-testid="sop-session-select"><SelectValue placeholder="Past sessions…" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {sessions.length === 0 && <div className="text-xs p-2 text-[#8B7355]">no past sessions</div>}
                  {sessions.map((s) => <SelectItem key={s.session_id} value={s.session_id}>{s.title}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={newChat} data-testid="sop-new-chat">+ New Chat</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 flex flex-col" style={{ height: 480 }}>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="sop-messages">
            {messages.length === 0 && (
              <div className="text-center py-16 text-[#8B7355]">
                <MessageCircle className="w-10 h-10 mx-auto mb-3 text-emerald-300" />
                <div className="text-sm">Ask about SOPs, HACCP, calibration schedule, complaints, or recall procedure.</div>
                <div className="text-xs mt-2 space-y-1">
                  <div className="italic">"What is the SOP for handling a rejected raw material?"</div>
                  <div className="italic">"What are our HACCP critical control points?"</div>
                  <div className="italic">"How do I initiate a batch recall?"</div>
                </div>
              </div>
            )}
            {messages.map((m, k) => (
              <div key={k} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#C05621] text-white" : "bg-[#FAF6EE] text-[#2C1810] border border-[#EAE5D9]"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {sending && <div className="text-xs text-[#8B7355] italic">assistant is thinking…</div>}
          </div>
          <div className="border-t border-[#EAE5D9] p-3 flex gap-2 items-end bg-white">
            <Textarea rows={2} value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask a question… (Shift+Enter for new line)" data-testid="sop-input" />
            <Button onClick={send} disabled={sending || !input.trim()} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="sop-send"><Send className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Upload SOP dialog */}
      <Dialog open={sopOpen} onOpenChange={setSopOpen}>
        <DialogContent className="max-w-xl bg-white">
          <DialogHeader><DialogTitle className="font-[Outfit]">Upload SOP</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><Label>Code*</Label><Input value={sopForm.code || ""} onChange={(e) => setSopForm({ ...sopForm, code: e.target.value })} placeholder="e.g. SOP-RM-02" data-testid="sop-form-code" /></div>
            <div><Label>Category</Label>
              <Select value={sopForm.category} onValueChange={(v) => setSopForm({ ...sopForm, category: v })}>
                <SelectTrigger data-testid="sop-form-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {["raw_material", "in_process", "finished", "hygiene", "ccp", "complaint", "calibration", "recall", "other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Title*</Label><Input value={sopForm.title || ""} onChange={(e) => setSopForm({ ...sopForm, title: e.target.value })} data-testid="sop-form-title" /></div>
            <div className="col-span-2"><Label>Body*</Label><Textarea rows={8} value={sopForm.body || ""} onChange={(e) => setSopForm({ ...sopForm, body: e.target.value })} placeholder="Full SOP text — the chatbot will cite this SOP by its code in answers." data-testid="sop-form-body" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSopOpen(false)}>Cancel</Button>
            <Button onClick={saveSop} className="bg-[#2C1810] text-white" data-testid="sop-form-save">Save SOP</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =====================================================================
// Phase 19-H · Weekly AI Quality Digest
// =====================================================================
function WeeklyDigestPanel() {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [newR, setNewR] = useState({ name: "", email: "", whatsapp: "", role: "qa_head" });

  const loadAll = async () => {
    try {
      const [l, h, r] = await Promise.all([
        api.get("/qcai/digest/latest"),
        api.get("/qcai/digest/"),
        api.get("/qcai/digest/recipients/list"),
      ]);
      setLatest(l.data?.digest || null);
      setHistory(h.data || []);
      setRecipients((r.data?.recipients) || []);
    } catch (e) { toast.error("Failed to load digest data"); }
  };
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (!selectedId) { setSelectedDoc(null); return; }
    api.get(`/qcai/digest/${selectedId}`).then((r) => setSelectedDoc(r.data)).catch(() => toast.error("Failed to load"));
  }, [selectedId]);

  const generateNow = async () => {
    if (!window.confirm("Generate a new digest now? This runs Claude 4.5 on up to 50 batches — takes ~2-8 minutes and will attempt to email/WhatsApp all recipients.")) return;
    setBusy(true);
    try {
      const res = await api.post("/qcai/digest/generate");
      const jobId = res.data?.job_id;
      toast("Generation queued in background — polling for result…", { icon: "⏳" });
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const j = await api.get(`/qcai/digest/jobs/${jobId}`);
          if (j.data?.status === "done") {
            toast.success(`Digest generated: ${(j.data.headline || "").slice(0, 60)}`);
            setBusy(false);
            await loadAll();
            return;
          }
          if (j.data?.status === "failed") {
            toast.error(`Digest failed: ${(j.data.error || "").slice(0, 120)}`);
            setBusy(false);
            return;
          }
        } catch (_) { /* ignore transient */ }
        if (attempts >= 90) {  // up to 15 min
          setBusy(false);
          toast("Still generating — check history in a minute", { icon: "⏳" });
          return;
        }
        setTimeout(poll, 10000);
      };
      setTimeout(poll, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start generation");
      setBusy(false);
    }
  };

  const resend = async (id) => {
    if (!window.confirm("Resend this digest to all recipients?")) return;
    try { const r = await api.post(`/qcai/digest/${id}/resend`); toast.success(`Resent to ${r.data?.recipient_count || 0}`); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Resend failed"); }
  };

  const addRecipient = () => {
    if (!newR.name || (!newR.email && !newR.whatsapp)) { toast.error("Name + email OR whatsapp required"); return; }
    setRecipients([...recipients, { ...newR }]);
    setNewR({ name: "", email: "", whatsapp: "", role: "qa_head" });
  };
  const removeRecipient = (i) => setRecipients(recipients.filter((_, k) => k !== i));

  const saveRecipients = async () => {
    try {
      const payload = { recipients: recipients.map((r) => ({ ...r, email: r.email || null, whatsapp: r.whatsapp || null })) };
      const r = await api.put("/qcai/digest/recipients/list", payload);
      toast.success(`Saved ${r.data?.count || 0} recipient(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="pt-4 space-y-6" data-testid="digest-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-[Outfit] text-xl font-semibold text-[#2C1810] flex items-center gap-2"><Send className="w-5 h-5 text-red-700" /> Weekly AI Quality Digest</h3>
          <p className="text-xs text-[#8B7355] mt-1">Auto-runs Monday 09:00 IST · Claude Sonnet 4.5 · Up to 50 batches analyzed · Delivered via Email + WhatsApp.</p>
        </div>
        <Button onClick={generateNow} disabled={busy} className="bg-red-700 hover:bg-red-800 text-white" data-testid="digest-generate">
          {busy ? "Generating…" : <><Sparkles className="w-3 h-3 mr-1" /> Generate Now</>}
        </Button>
      </div>

      {latest ? <DigestCard digest={latest} onResend={() => resend(latest.id)} /> :
        <Card className="border-[#EAE5D9]"><CardContent className="py-14 text-center text-[#8B7355]">
          No digest yet. Click <b>Generate Now</b> above or wait until next Monday 09:00 IST.
        </CardContent></Card>}

      {/* Recipients editor */}
      <Card className="border-[#EAE5D9]">
        <CardHeader className="pb-2"><CardTitle className="text-base font-[Outfit]">QA-Head Recipients (global)</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="digest-recipients-rows">
            <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase">
              <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Email</th><th className="text-left px-3 py-2">WhatsApp</th><th className="text-left px-3 py-2">Role</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {recipients.map((r, i) => (
                <tr key={i} className="border-t border-[#EAE5D9]">
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2 text-xs">{r.email || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono">{r.whatsapp || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.role || "—"}</td>
                  <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" className="text-red-600 h-6 px-2" onClick={() => removeRecipient(i)} data-testid={`digest-recip-remove-${i}`}>×</Button></td>
                </tr>
              ))}
              <tr className="border-t border-[#EAE5D9] bg-[#FAF6EE]/40">
                <td className="px-3 py-2"><Input value={newR.name} onChange={(e) => setNewR({ ...newR, name: e.target.value })} placeholder="Name" data-testid="digest-new-name" /></td>
                <td className="px-3 py-2"><Input value={newR.email} onChange={(e) => setNewR({ ...newR, email: e.target.value })} placeholder="email@company.com" data-testid="digest-new-email" /></td>
                <td className="px-3 py-2"><Input value={newR.whatsapp} onChange={(e) => setNewR({ ...newR, whatsapp: e.target.value })} placeholder="+919999999999" data-testid="digest-new-wa" /></td>
                <td className="px-3 py-2"><Input value={newR.role} onChange={(e) => setNewR({ ...newR, role: e.target.value })} placeholder="qa_head" data-testid="digest-new-role" /></td>
                <td className="px-3 py-2 text-right"><Button size="sm" onClick={addRecipient} className="bg-emerald-700 text-white" data-testid="digest-new-add">Add</Button></td>
              </tr>
            </tbody>
          </table>
        </CardContent>
        <div className="p-3 border-t border-[#EAE5D9] flex justify-end">
          <Button onClick={saveRecipients} className="bg-[#2C1810] text-white" data-testid="digest-recipients-save">Save Recipients</Button>
        </div>
      </Card>

      {/* History */}
      <Card className="border-[#EAE5D9]">
        <CardHeader className="pb-2"><CardTitle className="text-base font-[Outfit]">Digest History</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="digest-history-rows">
            <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase">
              <tr><th className="text-left px-3 py-2">Generated</th><th className="text-left px-3 py-2">Trigger</th><th className="text-left px-3 py-2">Headline</th><th className="text-right px-3 py-2">Batches</th><th className="text-right px-3 py-2">Delivered</th><th className="text-right px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-t border-[#EAE5D9] hover:bg-[#FAF6EE]/50 cursor-pointer" onClick={() => setSelectedId(h.id)}>
                  <td className="px-3 py-2 text-xs">{new Date(h.generated_at).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2"><Badge className={`border-0 text-[10px] ${h.triggered_by === "schedule" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}`}>{h.triggered_by}</Badge></td>
                  <td className="px-3 py-2 text-xs">{h.summary?.headline || "—"}</td>
                  <td className="px-3 py-2 text-right text-xs">{h.batch_risk_count ?? 0}</td>
                  <td className="px-3 py-2 text-right text-xs">{h.delivery_result?.recipient_count ?? 0}</td>
                  <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); resend(h.id); }} data-testid={`digest-resend-${h.id}`}>Resend</Button></td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#8B7355]">No past digests</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selectedId} onOpenChange={(o) => !o && setSelectedId(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-[Outfit] flex items-center gap-2"><Send className="w-4 h-4 text-red-700" /> Digest Detail</DialogTitle></DialogHeader>
          {selectedDoc ? <DigestCard digest={selectedDoc} compact /> : <div className="py-10 text-center text-[#8B7355]">Loading…</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DigestCard({ digest, onResend, compact = false }) {
  const s = digest.summary || {};
  const snap = digest.plant_snapshot || {};
  const dr = digest.delivery_result || {};
  return (
    <Card className={`border-red-200 ${compact ? "" : "bg-gradient-to-br from-red-50/40 to-white"}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs text-[#8B7355]">{new Date(digest.generated_at).toLocaleString("en-IN")} · {digest.triggered_by} · {digest.batch_risk_count ?? 0} batches analyzed</div>
            <CardTitle className="text-lg font-[Outfit] mt-1">🎯 {s.headline || "—"}</CardTitle>
          </div>
          {!compact && onResend && <Button size="sm" variant="outline" onClick={onResend} data-testid="digest-resend-current">Resend</Button>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm italic border-l-2 border-red-300 pl-3 text-[#2C1810]">{s.executive_summary}</p>

        {/* Plant snapshot */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="digest-snapshot">
          <MiniStat label="NCR" value={snap.ncr_count} tone="red" />
          <MiniStat label="Holds" value={snap.hold_count} tone="amber" />
          <MiniStat label="Complaints" value={snap.complaint_count} tone="red" />
          <MiniStat label="CCP fails" value={snap.ccp_fail_count} tone="red" />
          <MiniStat label="Env fails" value={snap.env_fail_count} tone="amber" />
          <MiniStat label="Rejected" value={snap.batches_rejected} tone="red" />
        </div>

        {/* Top actions */}
        {(s.top_3_actions || []).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8B7355] mb-1">Top Actions</div>
            <ol className="space-y-1.5 list-decimal list-inside">
              {(s.top_3_actions || []).map((a, k) => (
                <li key={k} className="text-sm flex gap-2 items-start ml-0">
                  <Badge className={`border-0 text-[10px] ${a.urgency === "urgent" ? "bg-red-600 text-white" : a.urgency === "high" ? "bg-red-100 text-red-800" : a.urgency === "medium" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>{a.urgency}</Badge>
                  <span>{a.action} <span className="text-[#8B7355] text-xs">({a.owner})</span></span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Top batches at risk */}
        {(s.top_batches_at_risk || []).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8B7355] mb-1">Top Batches at Risk</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-[#EAE5D9] rounded" data-testid="digest-top-batches">
                <thead className="bg-[#FAF6EE] text-[#8B7355] text-[10px] uppercase">
                  <tr><th className="text-left px-2 py-1.5">Batch</th><th className="text-left px-2 py-1.5">Product</th><th className="text-right px-2 py-1.5">Risk</th><th className="text-left px-2 py-1.5">Reason</th></tr>
                </thead>
                <tbody>
                  {(s.top_batches_at_risk || []).map((b, k) => (
                    <tr key={k} className="border-t border-[#EAE5D9]">
                      <td className="px-2 py-1.5 font-mono text-xs">{b.batch_no}</td>
                      <td className="px-2 py-1.5 text-xs">{b.product_name}</td>
                      <td className="px-2 py-1.5 text-right"><Badge className={`border-0 ${b.risk_level === "critical" ? "bg-red-600 text-white" : b.risk_level === "high" ? "bg-red-100 text-red-800" : b.risk_level === "medium" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{b.risk_score} · {b.risk_level}</Badge></td>
                      <td className="px-2 py-1.5 text-xs text-[#8B7355]">{b.primary_reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Trend flags */}
        {(s.trend_flags || []).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8B7355] mb-1">Trend Flags</div>
            <div className="flex gap-2 flex-wrap">{(s.trend_flags || []).map((f, k) => <Badge key={k} className="bg-orange-100 text-orange-800 border-0">{f}</Badge>)}</div>
          </div>
        )}

        {/* Delivery */}
        <div className="pt-2 border-t border-[#EAE5D9] text-xs text-[#8B7355]">
          Delivered to <b>{dr.recipient_count ?? 0}</b> recipient(s).
          {(dr.deliveries || []).map((d, k) => (
            <span key={k} className="ml-2">
              {d.name}: {d.email_result && <Badge className={`border-0 text-[10px] ${d.email_result.status === "sent" ? "bg-emerald-100 text-emerald-800" : d.email_result.status === "skipped" ? "bg-slate-100 text-slate-700" : "bg-red-100 text-red-800"}`}>email {d.email_result.status}</Badge>}
              {d.whatsapp_result && <Badge className={`border-0 text-[10px] ml-1 ${d.whatsapp_result.status === "sent" ? "bg-emerald-100 text-emerald-800" : d.whatsapp_result.status === "skipped" ? "bg-slate-100 text-slate-700" : "bg-red-100 text-red-800"}`}>whatsapp {d.whatsapp_result.status}</Badge>}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


// ==================== QC Team Management (Super Admin only) ====================
function QCTeamPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "qc_manager", password: "" });
  const [saving, setSaving] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers((data || []).filter((u) => u.role === "qc_manager" || u.role === "qc_staff"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load QC users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      toast.error("Name, email and password are all required");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/users", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone || undefined,
        role: form.role,
        password: form.password,
      });
      toast.success(`${form.role === "qc_manager" ? "Quality Manager" : "QC Support Staff"} account created`);
      setLastCreated({
        name: data.name, email: data.email, role: data.role, password: form.password,
      });
      setForm({ name: "", email: "", phone: "", role: form.role, password: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create user");
    } finally { setSaving(false); }
  };

  const disableUser = async (u) => {
    if (!window.confirm(`Disable QC login for ${u.email}?`)) return;
    try {
      await api.patch(`/users/${u.id}`, { is_active: false });
      toast.success("Disabled");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const enableUser = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: true });
      toast.success("Enabled");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const roleLabel = (r) => r === "qc_manager" ? "Quality Manager" : "QC Support Staff";

  return (
    <div className="space-y-6" data-testid="qc-team-panel">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="w-4 h-4 text-slate-700" />
            Create QC login
          </CardTitle>
          <p className="text-xs text-slate-500 pt-1">
            <b>Quality Manager</b> can enter data <b>AND</b> finalize/approve QC reports.
            <b> QC Support Staff</b> can only enter data — the "Approve" action is blocked (403) at the server.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Full name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="qc-user-name" placeholder="e.g. Ramesh Iyer" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Email (username) *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="qc-user-email" placeholder="qc.ramesh@bakemill.com" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="qc-user-phone" placeholder="+91-98..." />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="qc-user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="qc_manager">Quality Manager (can approve reports)</SelectItem>
                  <SelectItem value="qc_staff">QC Support Staff (data entry only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">Password *  <span className="text-slate-400">(min 8 chars — the user cannot change their role, only Super Admin can)</span></Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="qc-user-password" placeholder="e.g. QcPass2026!" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={saving} data-testid="qc-user-create" className="bg-slate-800 hover:bg-slate-900 text-white">
              {saving ? "Creating…" : "Create QC user"}
            </Button>
          </div>
          {lastCreated && (
            <div className="border border-emerald-300 bg-emerald-50 rounded p-3 text-sm">
              <div className="font-medium text-emerald-800 mb-1">
                {roleLabel(lastCreated.role)} created — share these once:
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                <div><span className="text-slate-500">Email:</span> {lastCreated.email}</div>
                <div><span className="text-slate-500">Password:</span> {lastCreated.password}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Existing QC users ({users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left uc-label border-b">
                  <th className="px-2 py-2">Name</th>
                  <th className="px-2 py-2">Email</th>
                  <th className="px-2 py-2">Role</th>
                  <th className="px-2 py-2">Phone</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-500">Loading…</td></tr>}
                {!loading && users.length === 0 && <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-500">No QC users yet.</td></tr>}
                {!loading && users.map((u) => (
                  <tr key={u.id} className="border-b hover:bg-slate-50" data-testid={`qc-user-row-${u.id}`}>
                    <td className="px-2 py-2 font-medium">{u.name}</td>
                    <td className="px-2 py-2 text-[12px] font-mono">{u.email}</td>
                    <td className="px-2 py-2">
                      {u.role === "qc_manager"
                        ? <Badge className="bg-emerald-100 border border-emerald-200 text-emerald-800">Quality Manager</Badge>
                        : <Badge className="bg-slate-100 border border-slate-200 text-slate-800">QC Support</Badge>}
                    </td>
                    <td className="px-2 py-2 text-[12px]">{u.phone || "—"}</td>
                    <td className="px-2 py-2">
                      {u.is_active !== false
                        ? <Badge className="bg-emerald-50 border border-emerald-200 text-emerald-700">Active</Badge>
                        : <Badge className="bg-red-50 border border-red-200 text-red-700">Disabled</Badge>}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {u.is_active !== false ? (
                        <Button size="sm" variant="outline" onClick={() => disableUser(u)} data-testid={`qc-user-disable-${u.id}`} className="border-red-300 text-red-600 hover:bg-red-50">Disable</Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => enableUser(u)} data-testid={`qc-user-enable-${u.id}`}>Enable</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

